/* istanbul ignore file */
import React, {
  forwardRef, useEffect, useContext, useRef, useMemo, useCallback,
} from 'react';
import _ from 'underscore';
import {
  MenuItem,
  MenuButton,
  Input,
  makeStyles,
  mergeClasses,
  shorthands,
  Popover,
  PopoverSurface,
  tokens,
  useIsOverflowItemVisible,
} from '@fluentui/react-components';
import type { PositioningImperativeRef } from '@fluentui/react-components';
import { SearchRegular } from '@fluentui/react-icons';
import { LayoutSize } from '@bingads-webui-fluent-component/layout-size';
import { CopilotIcon } from '@bingads-webui-react/copilot-ui-base';
import { usePrevious } from '@bingads-webui-react/primitive-hooks';
import { NavItem, MenuContext } from '@bingads-webui-fluent-component/global-menu-container';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { withHotKeys, ShortcutType } from '@bingads-webui-react/hot-keys';
import { isCopilotRegionRestricted } from '@bingads-webui-react/copilot-utils';

import type { II18n, SearchProps } from './types';

const TOTAL_SEARCH_RESULTS_NUM = 8;

const useStyles = makeStyles({
  searchContainer: {
    flexGrow: '1',
    flexShrink: '1',
    minWidth: '250px',
    maxWidth: '390px',
    marginRight: 'auto',
    ...shorthands.padding(tokens.spacingVerticalNone, tokens.spacingHorizontalXL),
  },
  single: {
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  searchInput: {
    width: '100%',
    ...shorthands.border(tokens.spacingHorizontalNone),

    '& input': {
      '&:focus, &:hover': {
        ...shorthands.border(tokens.spacingHorizontalNone),
      },
    },
  },
  searchInputOpened: {
    visibility: 'hidden',
  },
  popoverSurface: {
    ...shorthands.padding(tokens.spacingHorizontalNone),
    ...shorthands.border(tokens.spacingHorizontalNone),
  },
  shortcutItem: {
    display: 'flex',
    alignSelf: 'center',
  },
  shortcutKey: {
    display: 'inline-block',
    ...shorthands.border(tokens.strokeWidthThin, 'solid', '#979797'),
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: '16px',
    width: '14px',
    textAlign: 'center',
    '&:first-child': {
      marginRight: tokens.spacingHorizontalSNudge,
    },
  },
  shortcutCopilotContainer: {
    display: 'flex',
  },
  copilotIcon: {
    display: 'flex',
    paddingLeft: '6px',
  },
});

const inputId = 'global-search-input';

const SearchShortcutKey = () => {
  const classes = useStyles();
  return (
    <div className={mergeClasses('short-cut-key', classes.shortcutItem)}>
      <div className={classes.shortcutKey}>G</div>
      <div className={classes.shortcutKey}>T</div>
    </div>
  );
};

const CopilotLogo = ({
  classes, title, onCopilotClick, isNewLogo,
}) => {
  const openCopilot = () => onCopilotClick({ forceOpen: true });
  const onCopilotKeyDown = (e) => { if (e && e.key === 'Enter') { openCopilot(); } };

  return (
    <div role="button" tabIndex={0} className={mergeClasses(classes.copilotIcon, 'search-box-copilot-icon')} onClick={openCopilot} onKeyDown={onCopilotKeyDown}>
      <CopilotIcon
        size="medium"
        color={isNewLogo ? 'multicolor' : 'blue'}
        title={title}
      />
    </div>
  );
};

const Search :React.FC<SearchProps> = ({
  as = undefined, itemKey, renderSearchResults, siteMap, hotKey, count, onCopilotClick, serverContext,
  outsideRef: ref,
}) => {
  const {
    layoutSize,
    updateMode,
    singleComponentModeKey,
    updatePinedItemKey,
    dynamicallyPinedItemKey,
  } = useContext(MenuContext);
  const classes = useStyles();
  const [popoverWidth, setPopoverWidth] = React.useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const permissions = useContext(PermissionsContext);
  const i18n: II18n = useContext(I18nContext);
  const instrumentation = useContext(InstrumentationContext) as any;
  const [open, setOpen] = React.useState(false);
  const previousOpen = usePrevious(open);
  const previousCount = usePrevious(count);
  const isVisible = useIsOverflowItemVisible(itemKey);
  const [searchQuery, setSearchQuery] = React.useState('');
  const positioningRef = React.useRef<PositioningImperativeRef>(null);
  const popoverAnchorRef = React.useRef<HTMLDivElement>(null);
  const [isAdsCopilotEnabled, setIsAdsCopilotEnabled] = React.useState(undefined);

  const showInput = useMemo(
    () => layoutSize === LayoutSize.XXL
      || layoutSize === LayoutSize.XL
      || singleComponentModeKey === itemKey
      || dynamicallyPinedItemKey === itemKey,
    [dynamicallyPinedItemKey, itemKey, layoutSize, singleComponentModeKey],
  );

  useEffect(() => {
    if (singleComponentModeKey === itemKey || dynamicallyPinedItemKey === itemKey) {
      if (inputRef.current && showInput) {
        window.requestAnimationFrame(() => {
          setOpen(true);
        });
      }
    }
  }, [itemKey, singleComponentModeKey, inputRef, showInput, dynamicallyPinedItemKey]);

  useEffect(() => {
    if (!open && previousOpen) {
      if (showInput) {
        inputRef.current?.focus();
      } else {
        (ref as any)?.current?.focus();
      }
      if (singleComponentModeKey === itemKey) {
        updateMode(undefined);
      }
      if (dynamicallyPinedItemKey === itemKey) {
        updatePinedItemKey(undefined);
      }
    }
  }, [dynamicallyPinedItemKey, itemKey, open, previousOpen, ref, showInput, singleComponentModeKey, updateMode, updatePinedItemKey]);

  const clear = () => {
    setSearchQuery('');
    setOpen(false);
  };

  useEffect(() => {
    if (popoverAnchorRef.current && showInput && as === MenuButton && open) {
      positioningRef.current?.setTarget(popoverAnchorRef.current);
    } else {
      positioningRef.current?.setTarget(null);
    }
  }, [
    positioningRef,
    popoverAnchorRef,
    showInput,
    as,
    open,
    popoverWidth, // update positioning when popover width changes
  ]);

  const searchActivity = useMemo(() => {
    const scenario = instrumentation.createScenario({ scenarioName: ' globalSearchScenario' });
    const activity = scenario.createActivity('searchActivity');
    return activity;
  }, [instrumentation]);

  useEffect(() => {
    isCopilotRegionRestricted({
      feature: 'Chat',
      logger: searchActivity,
      permissions: permissions as any,
      serverContext,
    }).then((isRestricted) => {
      setIsAdsCopilotEnabled(!isRestricted);
    });
  }, [permissions, searchActivity, serverContext]);

  const onOpenChange = useCallback((event, data) => {
    let shouldOpen;
    if (!event.currentTarget) {
      shouldOpen = data.open;
    } else {
      const targetId = event.currentTarget.id;
      shouldOpen = data.open || targetId === inputId;
    }

    setOpen(shouldOpen);
    if (!shouldOpen) {
      if (showInput) {
        inputRef.current?.focus();
      }
      setSearchQuery('');
    }
  }, [showInput, setOpen, setSearchQuery]);

  const getSearchItemUniqueId = (item) => item && item.route;

  const searchResultsNum = Math.ceil(TOTAL_SEARCH_RESULTS_NUM / renderSearchResults.length);
  const searchResults: React.ReactNode[] = useMemo(() => renderSearchResults.map((renderSearch) => renderSearch({
    searchActivity,
    getSearchItemUniqueId,
    searchQuery,
    clear,
    siteMap,
    i18n,
    searchResultsNum,
  })), [i18n, renderSearchResults, searchActivity, searchQuery, searchResultsNum, siteMap]);

  useEffect(() => {
    if (showInput && popoverAnchorRef.current) {
      setPopoverWidth(popoverAnchorRef.current.offsetWidth);
    }
  }, [showInput, popoverAnchorRef, setPopoverWidth]);

  const onInputClick = () => {
    setOpen(true);
  };

  const onIconClick = () => {
    if (layoutSize === LayoutSize.S || layoutSize === LayoutSize.M) {
      updateMode(itemKey);
    }
    updatePinedItemKey(itemKey);
  };

  const onMenuItemClick = () => {
    updateMode(itemKey);
  };

  useEffect(() => {
    if (!isVisible) {
      setOpen(false);
    }
  }, [isVisible, setOpen]);

  useEffect(() => {
    if (hotKey === 'g+t' && count !== previousCount) {
      if (as === MenuButton) {
        if (showInput) {
          setOpen(true);
        } else {
          onIconClick();
        }
      } else {
        onMenuItemClick();
      }
    }
  }, [count, hotKey, previousCount, setOpen]);

  if (as === MenuButton) {
    if (showInput) {
      const copilotLogo = isAdsCopilotEnabled ? (
        <CopilotLogo
          classes={classes}
          title={i18n.getString(_TL_('Copilot icon'))}
          onCopilotClick={onCopilotClick}
          isNewLogo={_.get(permissions, ['dynamic', 'AdsCopilotUxImprovements'])}
        />
      ) : undefined;

      return (
        <div className={mergeClasses(classes.searchContainer, 'search-box', singleComponentModeKey === itemKey ? classes.single : '')} ref={ref}>
          <div style={{ visibility: 'hidden' }} ref={popoverAnchorRef} />
          <Popover
            onOpenChange={onOpenChange}
            open={open}
            positioning={{ positioningRef }}
            trapFocus
          >
            <Input
              className={mergeClasses(classes.searchInput, open ? classes.searchInputOpened : '')}
              contentBefore={<SearchRegular />}
              contentAfter={isAdsCopilotEnabled && copilotLogo}
              placeholder={i18n.getString(_TL_('Search Microsoft Advertising'))}
              ref={inputRef}
              value={searchQuery}
              onClick={onInputClick}
              onChange={(e, data) => {
                setOpen(true);
                setSearchQuery(data.value);
              }}
              id={inputId}
            />
            <PopoverSurface className={mergeClasses('search-popover', classes.popoverSurface)} style={{ width: popoverWidth }}>
              <Input
                className={classes.searchInput}
                contentBefore={<SearchRegular />}
                contentAfter={(
                  <div className={classes.shortcutCopilotContainer}>
                    <SearchShortcutKey />
                    {isAdsCopilotEnabled && copilotLogo}
                  </div>
                )}
                value={searchQuery}
                placeholder={i18n.getString(_TL_('Search Microsoft Advertising'))}
                onChange={(e, data) => { setSearchQuery(data.value); }}
              />
              {searchResults}
            </PopoverSurface>
          </Popover>
        </div>
      );
    }

    return (
      <div ref={ref}>
        <NavItem
          icon={SearchRegular}
          text={i18n.getString(_TL_('Search'))}
          onClick={onIconClick}
          as={MenuButton}
          itemKey="search"
        />
      </div>
    );
  }

  return (
    <MenuItem
      icon={<SearchRegular />}
      className="search-menu-item"
      ref={ref}
      onClick={onMenuItemClick}
    >
      {i18n.getString(_TL_('Search'))}
    </MenuItem>
  );
};

const SearchWithHotKeys = withHotKeys(Search, ShortcutType.GlobalSearch);
const SearchWithHotKeysWithForwardRef = forwardRef<HTMLDivElement, SearchProps>(
  (props, ref) => <SearchWithHotKeys {...props} outsideRef={ref} />,
);

export { SearchWithHotKeysWithForwardRef as Search };
